import { type ZapBreakdownDetails, type Template } from "@zapier/templates";
import {
  type AppImplementationResponse,
  getImplementationDetails,
} from "./getImplementationDetails";
import { searchApps } from "./search";
import { isZSLAssetWithZdl } from "src/utils/zslTypeguard";
import { isZapTemplate } from "src/utils/isZapTemplate";

type Step = {
  app: string;
  action: string;
};

const getAppFromStep = (
  step: Step,
  implementations: AppImplementationResponse["results"],
) => {
  return (
    implementations.find((app) => {
      return (
        app.selected_api.split("@")[0].replace("CLIAPI", "API") ===
        step.app.split("@")[0].replace("CLIAPI", "API")
      );
    }) ?? null
  );
};

export async function getZapBreakdown(
  template: Template,
): Promise<ZapBreakdownDetails | null> {
  const firstZap = template.zsl?.zaps?.[0];
  const templateSteps: Step[] =
    isZapTemplate(template) && firstZap && isZSLAssetWithZdl(firstZap)
      ? firstZap.zdl.steps.map((step: { app: string; action: string }) => ({
          app: step.app,
          action: step.action,
        }))
      : [];

  if (templateSteps.length === 0) return null;

  const slugs = template.apps ?? [];
  const apps = await searchApps({
    slugs: Array.from(slugs).join(","),
    limit: String(slugs.length),
  });
  const implementations = await getImplementationDetails({
    selectedApis: apps.map((app) => app.current_implementation_id),
  });

  const firstApp = getAppFromStep(templateSteps[0], implementations);
  const lastApp = getAppFromStep(
    templateSteps[templateSteps.length - 1],
    implementations,
  );

  return {
    title: template.name ?? "",
    firstStep: firstApp
      ? {
          serviceName: firstApp.name,
          serviceIcon: firstApp.images.url_16x16,
          stepLabel:
            firstApp.actions.find(
              (action) => action.key === templateSteps[0].action,
            )?.name ?? "",
        }
      : null,
    lastStep: lastApp
      ? {
          serviceName: lastApp.name,
          serviceIcon: lastApp.images.url_16x16,
          stepLabel:
            lastApp.actions.find(
              (action) =>
                action.key === templateSteps[templateSteps.length - 1].action,
            )?.name ?? "",
        }
      : null,
    totalStepCount: templateSteps.length,
    services:
      template.apps?.map((app: string) => ({
        name: implementations.find((impl) => impl.slug === app)?.name ?? "",
        src:
          implementations.find((impl) => impl.slug === app)?.images.url_16x16 ??
          "",
      })) ?? [],
    servicesList: template.apps ?? [],
  };
}
